import { deserializeObject, serializeObject } from '@app/shared';
import { SearchState } from './company-context.model';
import { fromApiFilter, toApiFilter } from './query-api';
import { formatQuery, parseQuery } from './query-parser';
import { FilterCondition, QueryContext, QueryError } from './query.model';

/**
 * The query parameters of the search page: `q`, `sort`, `ids`; `page` of
 * former URLs is read and dropped.
 */
export interface SearchParams {
  q?: string;
  sort?: string;
  page?: string;
  ids?: string;
}

/**
 * The search in readable query parameters: the filter as query, the sort as
 * `field` or `-field`. The companies of an alert can be many
 * and go compressed into `ids`. Tokens the parser could not read are kept in
 * `q`, so the bar shows them again.
 */
export const searchParams = (
  state: SearchState | null,
  context: QueryContext = {},
  invalid: string[] = []
): SearchParams => {
  if (!state) {
    return {};
  }
  const conditions = fromApiFilter(state.filterValue);
  const ids = conditions.find(
    (x) => x.field === 'business_id' && x.value.kind === 'multi'
  );
  const q = [formatQuery(conditions, context), ...invalid]
    .filter((x) => x)
    .join(' ');
  return {
    ...(q ? { q } : {}),
    ...(state.sort
      ? { sort: `${state.sort.order === -1 ? '-' : ''}${state.sort.field}` }
      : {}),
    ...(ids && ids.value.kind === 'multi'
      ? { ids: serializeObject({ ids: ids.value.values }) ?? undefined }
      : {}),
  };
};

export interface ParsedSearch {
  /** null without any parameter: the page shows presets, no search. */
  state: SearchState | null;
  errors: QueryError[];
}

/** The search of the query parameters of the search page. */
export const searchFromParams = (
  params: { get(name: string): string | null },
  context: QueryContext = {}
): ParsedSearch => {
  const q = params.get('q') ?? '';
  const sort = params.get('sort');
  const page = params.get('page');
  const idsParam = params.get('ids');
  if (!q.trim() && !sort && !page && !idsParam) {
    return { state: null, errors: [] };
  }
  const { conditions, errors } = parseQuery(q, context);
  const ids = deserializeObject<{ ids?: unknown }>(idsParam)?.ids;
  const all: FilterCondition[] = [
    ...conditions,
    ...(Array.isArray(ids) && ids.length
      ? [
          {
            field: 'business_id' as const,
            value: {
              kind: 'multi' as const,
              values: ids.filter(
                (x): x is string | number =>
                  typeof x === 'string' || typeof x === 'number'
              ),
            },
          },
        ]
      : []),
  ];
  return {
    state: {
      filterValue: all.length ? toApiFilter(all) : null,
      sort:
        sort && /^-?[A-Za-z_][\w.]*$/.test(sort)
          ? {
              field: sort.replace(/^-/, ''),
              order: sort.startsWith('-') ? -1 : 1,
            }
          : null,
    },
    errors,
  };
};
